import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TextField,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  IconButton
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIcon from "@mui/icons-material/Phone";
import { useDispatch, useSelector } from "react-redux";
import { fetchSupWiseOT } from "../../actions/SupervisiorWiseOTAction";
import { fetchLocationNotOff } from "../../actions/LocationNotOffAction";
import { fetchOTEmpNotLeft } from "../../actions/OTEmpNotLeftAction";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import homeImage from "../../assets/DTS2.png";


const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "650px",
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
  width: "100%",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "14px 10px",
  fontSize: "14px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154C79",
    color: "#FFFFFF",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.09)",
    cursor: "pointer",
  },
}));

export default function SupervisorWiseOT() {
  const dispatch = useDispatch();
  const { loading: supLoading, data: supData } = useSelector((state) => state.supervisorWiseOT);
  const { loading: locLoading, data: locData } = useSelector((state) => state.locationNotOff);
  const { loading: detailLoading, data: detailData } = useSelector((state) => state.otEmpNotLeft);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("supervisor"); // supervisor or location
  const [showDetail, setShowDetail] = useState(false);

const formatDate = (dateObj) => {
  const d = new Date(dateObj);
  return d.toISOString().split("T")[0];
};

const formatTime = (dateObj) => {
  const d = new Date(dateObj);
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};


  useEffect(() => {
    const today = new Date();
    setSelectedDate(today); 
    const formattedToday = formatDate(today);
    
    if (viewMode === "supervisor") {
      dispatch(fetchSupWiseOT(formattedToday));
    } else {
      dispatch(fetchLocationNotOff(formattedToday));
    }
  }, [dispatch, viewMode]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    const formatted = formatDate(date);
    if (viewMode === "supervisor") {
      dispatch(fetchSupWiseOT(formatted));
    } else {
      dispatch(fetchLocationNotOff(formatted));
    }
  };

  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
    setShowDetail(false);
  };

  const handleNotLeftClick = () => {
    setShowDetail(true);
    dispatch(fetchOTEmpNotLeft(formatDate(selectedDate)));
  };

  const handleBackClick = () => {
    setShowDetail(false);
  };

  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
  }, [viewMode]);

  const currentData = viewMode === "supervisor" ? supData : locData;
  const loading = viewMode === "supervisor" ? supLoading : locLoading;

  // ================= PDF EXPORT FOR SUPERVISOR =================
const handleExportSupervisorPDF = () => {
  const doc = new jsPDF("p", "mm", "a4");
  const generatedAt = new Date();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header Image
  doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

  // Title - Centered and Bold
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(21, 76, 121);
  doc.text("OT Allocated Employees - Supervisor Wise", pageWidth / 2, 75, { align: "center" });

  // Divider line
  doc.setDrawColor(21, 76, 121);
  doc.setLineWidth(0.5);
  doc.line(15, 82, pageWidth - 15, 82);

  const totalAssignCount = supData.reduce(
    (sum, item) => sum + Number(item.Sup_totalCount || 0),
    0
  );
  const totalNotLeftCount = supData.reduce(
    (sum, item) => sum + Number(item.Sup_empCount || 0),
    0
  );

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);

  const startX = 15;
  const endX = pageWidth - 15;
  const totalWidth = endX - startX;
  const yPosition = 88;

  // Calculate positions - divide into 4 equal sections (centered)
  const section1 = startX + (totalWidth * 0.125);  // 12.5% (center of first quarter)
  const section2 = startX + (totalWidth * 0.375);  // 37.5% (center of second quarter)
  const section3 = startX + (totalWidth * 0.625);  // 62.5% (center of third quarter)
  const section4 = startX + (totalWidth * 0.875);  // 87.5% (center of fourth quarter)

  // Date - centered in section 1
  const dateText = "Date -  " + formatDate(selectedDate);
  const dateWidth = doc.getTextWidth(dateText);
  doc.setFont("helvetica", "bold");
  doc.text("Date -  ", section1 - (dateWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(selectedDate), section1 - (dateWidth / 2) + doc.getTextWidth("Date -  "), yPosition);

  // Time - centered in section 2
  const timeText = "Time -  " + formatTime(generatedAt);
  const timeWidth = doc.getTextWidth(timeText);
  doc.setFont("helvetica", "bold");
  doc.text("Time -  ", section2 - (timeWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(formatTime(generatedAt), section2 - (timeWidth / 2) + doc.getTextWidth("Time -  "), yPosition);

  // Assigned - centered in section 3
  const assignedText = "Assigned -  " + totalAssignCount.toString();
  const assignedWidth = doc.getTextWidth(assignedText);
  doc.setFont("helvetica", "bold");
  doc.text("Assigned -  ", section3 - (assignedWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(totalAssignCount.toString(), section3 - (assignedWidth / 2) + doc.getTextWidth("Assigned -  "), yPosition);

  // Not Left - centered in section 4
  const notLeftText = "Not Left -  " + totalNotLeftCount.toString();
  const notLeftWidth = doc.getTextWidth(notLeftText);
  doc.setFont("helvetica", "bold");
  doc.text("Not Left -  ", section4 - (notLeftWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(totalNotLeftCount.toString(), section4 - (notLeftWidth / 2) + doc.getTextWidth("Not Left -  "), yPosition);

  const columns = ["Supervisor Name", "Total Assign", "Not Left"];
  const rows = supData.map((item) => [
    item.Sup_name || "",
    item.Sup_totalCount || "",
    item.Sup_empCount || "",
  ]);

  rows.push([
    "TOTAL",
    totalAssignCount.toString(),
    totalNotLeftCount.toString(),
  ]);

  autoTable(doc, {
    head: [columns],
    body: rows,
    startY: 95,
    styles: {
      fontSize: 12,
      cellPadding: 5,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [21, 76, 121],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10,
      halign: 'center',
      cellPadding: 6,
    },
    alternateRowStyles: {
      fillColor: [248, 249, 252],
    },
    columnStyles: {
      0: { cellWidth: 107, halign: "left" },
      1: { cellWidth: 40, halign: "center" },
      2: { cellWidth: 40, halign: "center" },
    },
    margin: { left: 12, right: 13 },
    theme: 'grid',
    didParseCell(data) {
      if (data.row.index === rows.length - 1) {
        data.cell.styles.fillColor = [21, 76, 121];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = "bold";
      }
    },
    didDrawPage: function(data) {
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.setFont("helvetica", "italic");
      const footerText = `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`;
      doc.text(footerText, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
      
      doc.text(
        `Page ${data.pageNumber}`,
        pageWidth - 20,
        doc.internal.pageSize.getHeight() - 10
      );
    }
  });

  doc.save(`OT_Supervisor_${formatDate(selectedDate)}.pdf`);
};


// ================= PDF EXPORT FOR LOCATION =================
const handleExportLocationPDF = () => {
  const doc = new jsPDF("p", "mm", "a4"); 
  const generatedAt = new Date();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header Image
  doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

  // Title - Centered and Bold
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(21, 76, 121);
  doc.text("OT Allocated Employees - Location Wise", pageWidth / 2, 75, { align: "center" });

  // Divider line
  doc.setDrawColor(21, 76, 121);
  doc.setLineWidth(0.5);
  doc.line(15, 82, pageWidth - 15, 82);
  
  // Calculate totals
  const totalAssignCount = locData.reduce((sum, item) => sum + Number(item.Loc_totalCount || 0), 0);
  const totalNotLeftCount = locData.reduce((sum, item) => sum + Number(item.Loc_empCount || 0), 0);

  // Date, Time, Total Assigned, and Not Left in a row
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);

  const startX = 15;
  const endX = pageWidth - 15;
  const totalWidth = endX - startX;
  const yPosition = 88;

  // Calculate positions - divide into 4 equal sections (centered)
  const section1 = startX + (totalWidth * 0.125);  // 12.5% (center of first quarter)
  const section2 = startX + (totalWidth * 0.375);  // 37.5% (center of second quarter)
  const section3 = startX + (totalWidth * 0.625);  // 62.5% (center of third quarter)
  const section4 = startX + (totalWidth * 0.875);  // 87.5% (center of fourth quarter)

  // Date - centered in section 1
  const dateText = "Date -  " + formatDate(selectedDate);
  const dateWidth = doc.getTextWidth(dateText);
  doc.setFont("helvetica", "bold");
  doc.text("Date - ", section1 - (dateWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(selectedDate), section1 - (dateWidth / 2) + doc.getTextWidth("Date -  "), yPosition);

  // Time - centered in section 2
  const timeText = "Time -  " + formatTime(generatedAt);
  const timeWidth = doc.getTextWidth(timeText);
  doc.setFont("helvetica", "bold");
  doc.text("Time -  ", section2 - (timeWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(formatTime(generatedAt), section2 - (timeWidth / 2) + doc.getTextWidth("Time -  "), yPosition);

  // Assigned - centered in section 3
  const assignedText = "Assigned -  " + totalAssignCount.toString();
  const assignedWidth = doc.getTextWidth(assignedText);
  doc.setFont("helvetica", "bold");
  doc.text("Assigned -  ", section3 - (assignedWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(totalAssignCount.toString(), section3 - (assignedWidth / 2) + doc.getTextWidth("Assigned -  "), yPosition);

  // Not Left - centered in section 4
  const notLeftText = "Not Left -  " + totalNotLeftCount.toString();
  const notLeftWidth = doc.getTextWidth(notLeftText);
  doc.setFont("helvetica", "bold");
  doc.text("Not Left -  ", section4 - (notLeftWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(totalNotLeftCount.toString(), section4 - (notLeftWidth / 2) + doc.getTextWidth("Not Left -  "), yPosition);

  const columns = ["Location", "Total Assign", "Not Left"];
  const rows = locData?.map(item => [
    item.Loc_location || "",
    item.Loc_totalCount || "",
    item.Loc_empCount || ""
  ]);
  
  rows.push([
    "TOTAL",
    totalAssignCount.toString(),
    totalNotLeftCount.toString()
  ]);

  autoTable(doc, {
    head: [columns],
    body: rows,
    startY: 95,
    styles: {
      fontSize: 12,
      cellPadding: 5,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [21, 76, 121],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10,
      halign: 'center',
      cellPadding: 6,
    },
    alternateRowStyles: {
      fillColor: [248, 249, 252],
    },
    columnStyles: {
      0: { cellWidth: 40, halign: 'center' },
      1: { cellWidth: 40, halign: 'center' },
      2: { cellWidth: 40, halign: 'center' },
    },
    margin: { left: 45, right: 10 },
    theme: 'grid',
    didParseCell: function(data) {
      if (data.row.index === rows.length - 1) {
        data.cell.styles.fillColor = [21, 76, 121];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
      }
    },
    didDrawPage: function(data) {
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.setFont("helvetica", "italic");
      const footerText = `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`;
      doc.text(footerText, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
      
      doc.text(
        `Page ${data.pageNumber}`,
        pageWidth - 20,
        doc.internal.pageSize.getHeight() - 10
      );
    }
  });

  doc.save(`OT_Location_${formatDate(selectedDate)}.pdf`);
};

// ================= PDF EXPORT FOR DETAIL =================
const handleExportDetailPDF = () => {
  const doc = new jsPDF("p", "mm", "a4");
  const generatedAt = new Date();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header Image
  doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

  // Title - Centered and Bold
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(21, 76, 121);
  doc.text("Employee Overtime Engagement", pageWidth / 2, 75, { align: "center" });

  // Divider line
  doc.setDrawColor(21, 76, 121);
  doc.setLineWidth(0.5);
  doc.line(15, 82, pageWidth - 15, 82);

  // Date, Time, and Total Employees in a row
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);

  const startX = 15;
  const endX = pageWidth - 15;
  const totalWidth = endX - startX;
  const yPosition = 88;

  // Calculate positions - divide into 3 equal sections (centered)
  const section1 = startX + (totalWidth * 0.15);  // 16.7% (center of first third)
  const section2 = startX + (totalWidth * 0.45);  // 50% (center of second third)
  const section3 = startX + (totalWidth * 0.80);  // 83.3% (center of third third)

  // Date - centered in section 1
  const dateText = "Date -  " + formatDate(selectedDate);
  const dateWidth = doc.getTextWidth(dateText);
  doc.setFont("helvetica", "bold");
  doc.text("Date - ", section1 - (dateWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(selectedDate), section1 - (dateWidth / 2) + doc.getTextWidth("Date -  "), yPosition);

  // Time - centered in section 2
  const timeText = "Time -  " + formatTime(generatedAt);
  const timeWidth = doc.getTextWidth(timeText);
  doc.setFont("helvetica", "bold");
  doc.text("Time - ", section2 - (timeWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(formatTime(generatedAt), section2 - (timeWidth / 2) + doc.getTextWidth("Time -  "), yPosition);

  // Total Employees - centered in section 3
  const totalText = "Total Employees -    " + (detailData?.length || 0).toString();
  const totalTextWidth = doc.getTextWidth(totalText);
  doc.setFont("helvetica", "bold");
  doc.text("Total Employees -   ", section3 - (totalTextWidth / 2), yPosition);
  doc.setFont("helvetica", "normal");
  doc.text((detailData?.length || 0).toString(), section3 - (totalTextWidth / 2) + doc.getTextWidth("Total Employees -    "), yPosition);

  autoTable(doc, {
    startY: 95,
    head: [[
      "Location",
      "Supervisor",
      "Service No",
      "Name",
      "CDL Job",
    ]],
    body: detailData?.map((item) => [
      item.Location || "",
      item.Supervisorname || "",
      item.EmployeeServiceNo || "",
      item.EmployeeName || "",
      item.CDLjob || "",
    ]),
    styles: {
      fontSize: 12,
      cellPadding: 5,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [21, 76, 121],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10,
      halign: 'center',
      cellPadding: 6,
    },
    alternateRowStyles: {
      fillColor: [248, 249, 252],
    },
    columnStyles: {
      0: { cellWidth: 25, halign: 'center' },
      1: { cellWidth: 40, halign: 'left' },
      2: { cellWidth: 28, halign: 'center' },
      3: { cellWidth: 45, halign: 'left' },
      4: { cellWidth: 50, halign: 'left' },
    },
    margin: { left: 15, right: 10 },
    theme: 'grid',
    didDrawPage: function(data) {
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.setFont("helvetica", "italic");
      const footerText = `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`;
      doc.text(footerText, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
      
      doc.text(
        `Page ${data.pageNumber}`,
        pageWidth - 20,
        doc.internal.pageSize.getHeight() - 10
      );
    }
  });

  doc.save(`OT_Employees_Not_Left_Details_${formatDate(selectedDate)}.pdf`);
};

  const handleExportPDF = () => {
    if (showDetail) {
      handleExportDetailPDF();
    } else if (viewMode === "supervisor") {
      handleExportSupervisorPDF();
    } else {
      handleExportLocationPDF();
    }
  };

  // Show detail view
  if (showDetail) {
    // Sort detail data by CDL Job
const sortedDetailData = detailData
  ? [...detailData].sort((a, b) => {
      const extractCode = (job) =>
        (job || "")
          .split("/")      // take before first /
          [0]
          .trim()
          .toUpperCase();

      const codeA = extractCode(a.CDLjob);
      const codeB = extractCode(b.CDLjob);

      const priority = ["NC", "SR", "OR"];

      const pA = priority.indexOf(codeA);
      const pB = priority.indexOf(codeB);

      // Both are priority jobs
      if (pA !== -1 && pB !== -1) return pA - pB;

      // Only A is priority
      if (pA !== -1) return -1;

      // Only B is priority
      if (pB !== -1) return 1;

      // Others → alphabetical by code
      return codeA.localeCompare(codeB);
    })
  : [];

    const standardMobile = (mobile) => {
      if (!mobile) return "";

      let m = mobile.toString().trim();

      m = m.replace(/[\s-]/g, "");

      // +94XXXXXXXXX → 0XXXXXXXXX
      if (m.startsWith("+94")) {
        return "0" + m.substring(3);
      }

      // 94XXXXXXXXX → 0XXXXXXXXX
      if (m.startsWith("94")) {
        return "0" + m.substring(2);
      }

      // Already correct
      if (m.startsWith("0")) {
        return m;
      }

      return m;
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <PageContainer sx={{ maxWidth: "1100px" }}>
          {/* Title with Back Button */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IconButton
              onClick={handleBackClick}
              sx={{
                backgroundColor: "#154C79",
                color: "#fff",
                marginRight: "12px",
                "&:hover": { backgroundColor: "#0e3c66" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#0E3C66",
                letterSpacing: "1px",
                flex: 1,
                textAlign: "center",
                marginRight: "48px",
              }}
            >
              OT Employees Not Left - Details
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ marginBottom: 2 }}>
            {/* Date Display */}
            <Grid item xs={6}>
              <Box
                sx={{
                  background: "#142c54",
                  color: "#fff",
                  borderRadius: "20px",
                  height: "56px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                {formatDate(selectedDate)}
              </Box>
            </Grid>

            {/* Total Count */}
            <Grid item xs={6}>
              <Box
                sx={{
                  background: "#e8f0fa",
                  borderRadius: "20px",
                  height: "56px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                Total: {sortedDetailData?.length || 0}
              </Box>
            </Grid>
          </Grid>

          {/* Export PDF Button */}
          <Grid container>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportPDF}
                disabled={detailLoading || !sortedDetailData?.length}
                sx={{
                  width: "100%",
                  height: "56px",
                  borderRadius: "20px",
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  fontWeight: "bold",
                  mb: 2,
                  textTransform: "none",
                  fontSize: { xs: "13px", sm: "16px" },
                  "&:hover": { backgroundColor: "#1b5e20" },
                  "&:disabled": {
                    backgroundColor: "#cccccc",
                    color: "#666666",
                  },
                }}
              >
                Export PDF
              </Button>
            </Grid>
          </Grid>

          {/* Detail Table with Horizontal Scroll */}
          <StyledTableContainer 
            component={Paper}
            sx={{
              overflowX: "auto", // horizontal scroll
              overflowY: "auto",
              width: "100%",
            }}
          >
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ textAlign: "center", minWidth: 100 }}>Location</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center", minWidth: 150 }}>Supervisor Name</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center", minWidth: 120 }}>Service No</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center", minWidth: 150 }}>Name</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center", minWidth: 200 }}>CDL Job</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!detailLoading && sortedDetailData?.length > 0 ? (
                  sortedDetailData.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell sx={{ textAlign: "center" }}>
                        {item.Location}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontWeight: 500, flex: 1 }}>
                          {item.Supervisorname}
                        </Typography>
                        <Box sx={{ width: "32px", display: "flex", justifyContent: "center" }}>
                          {item.Sup_Mobile && (
                            <IconButton
                              size="small"
                              component="a"
                              href={`tel:${standardMobile(item.Sup_Mobile)}`}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                backgroundColor: "#4CAF50",
                                color: "#fff",
                                width: "32px",
                                height: "32px",
                                "&:hover": {
                                  backgroundColor: "#45a049",
                                },
                              }}
                              aria-label={`Call ${item.Supervisorname}`}
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "center" }}>
                        {item.EmployeeServiceNo}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontWeight: 500, flex: 1 }}>
                          {item.EmployeeName}
                        </Typography>
                        <Box sx={{ width: "32px", display: "flex", justifyContent: "center" }}>
                          {item.Emp_Mobile && (
                            <IconButton
                              size="small"
                              component="a"
                              href={`tel:${standardMobile(item.Emp_Mobile)}`}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                backgroundColor: "#4CAF50",
                                color: "#fff",
                                width: "32px",
                                height: "32px",
                                "&:hover": {
                                  backgroundColor: "#45a049",
                                },
                              }}
                              aria-label={`Call ${item.EmployeeName}`}
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>
                        {item.CDLjob}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={5}>
                      {detailLoading ? "Loading..." : "No Records Found"}
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </PageContainer>
      </LocalizationProvider>
    );
  }

  // Original summary view
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        {/* Title */}
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#0E3C66",
            textAlign: "center",
            mb: 2,
            letterSpacing: "1px",
          }}
        >
          OT Allocated Employees
        </Typography>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          {/* Date Select Button */}
          <Grid item xs={6}>
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon />}
              onClick={() => setOpenDatePicker(true)}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#142c54",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "16px",
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
            >
              {formatDate(selectedDate)}
            </Button>
          </Grid>

          {/* View Mode Dropdown */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, color: "#154C79" }}>
                View Type
              </InputLabel>
              <Select
                value={viewMode}
                onChange={handleViewModeChange}
                label="View Type"
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  borderRadius: "20px",
                  backgroundColor: "#F0F4F8",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  "& .MuiSelect-icon": { color: "#154C79" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#154C79" },
                }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 250, overflowY: "auto" } },
                }}
              >
                <MenuItem value="supervisor">Based on Supervisor</MenuItem>
                <MenuItem value="location">Based on Location</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Date Picker */}
        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
          />
        )}

        {/* Total Employee Count */}
        {!loading && currentData?.length > 0 && (() => {
          const countKey = viewMode === "supervisor" ? "Sup_totalCount" : "Loc_totalCount";
          const empCountKey = viewMode === "supervisor" ? "Sup_empCount" : "Loc_empCount";
          const assignCount = currentData.reduce((sum, item) => sum + Number(item[countKey] || 0), 0);
          const notLeftCount = currentData.reduce((sum, item) => sum + Number(item[empCountKey] || 0), 0);

          return (
            <Box sx={{ display: "flex", justifyContent: "center", gap: "20px", mb: 2 }}>
              <Box sx={{ background: "#e8f0fa", border: "1px solid #bbd0ea", padding: "8px 14px", borderRadius: "8px", textAlign: "center", fontWeight: 600, color: "#0E3C66" }}>
                Assigned Count: {assignCount}
              </Box>
                <Box
                  onClick={() => {
                    setTimeout(() => {
                      handleNotLeftClick();
                    }, 250);
                  }}
                  sx={{
                    background: "#e8f0fa",
                    border: "1px solid #bbd0ea",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#0E3C66",
                    cursor: "pointer",
                    transition: "all 0.1s ease",
                    "&:hover": {
                      background: "#154C79",
                      color: "#fff",
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  Not Left Count: {notLeftCount}
                </Box>
            </Box>
          );
        })()}

        {/* Export PDF Button */}
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportPDF}
              disabled={loading || !currentData?.length}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#2e7d32",
                color: "#fff",
                fontWeight: "bold",
                mb: 2,
                textTransform: "none",
                fontSize: { xs: "13px", sm: "16px" },
                "&:hover": { backgroundColor: "#1b5e20" },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
            >
              Export PDF
            </Button>
          </Grid>

        {/* Table */}
        <StyledTableContainer component={Paper}>
            <Table sx={{ minWidth: "auto", width: "100%", tableLayout: "auto" }}>
            <TableHead>
              <TableRow>
                {viewMode === "supervisor" ? (
                  <>
                    <StyledTableCell sx={{ textAlign: "left" }}>Supervisor Name</StyledTableCell>
                  </>
                ) : (
                  <StyledTableCell sx={{ textAlign: "center" }}>Location</StyledTableCell>
                )}
                <StyledTableCell sx={{ textAlign: "center" }}>Total Assign</StyledTableCell>
                <StyledTableCell sx={{ textAlign: "center" }}>Not Left</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && currentData?.length > 0 &&
                currentData.map((item, index) => (
                  <StyledTableRow key={index}>
                    {viewMode === "supervisor" ? (
                      <>
                        <StyledTableCell sx={{ textAlign: "left" }}>{item.Sup_name}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Sup_totalCount}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Sup_empCount}</StyledTableCell>
                      </>
                    ) : (
                      <>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Loc_location}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Loc_totalCount}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Loc_empCount}</StyledTableCell>
                      </>
                    )}
                  </StyledTableRow>
                ))}

              {loading && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              )}

              {!loading && (!currentData || currentData.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ padding: "40px", fontSize: "16px", color: "#666" }}>
                    No Records Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </PageContainer>
    </LocalizationProvider>
  );
}